import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface DailyTraffic {
  date: string;
  label: string;
  visits: number;
  unique: number;
  pageViews: number;
}

export interface TopPage {
  page_path: string;
  views: number;
}

export interface TopProduct {
  product_name: string;
  product_slug: string;
  views: number;
}

export interface ReferrerSource {
  source: string;
  count: number;
}

export interface HourlyTraffic {
  hour: string;
  count: number;
}

export interface GeoData {
  country: string;
  city: string;
  count: number;
}

export interface PeriodComparison {
  visits: number;
  uniqueVisitors: number;
  pageViews: number;
  avgSessionDuration: string;
  bounceRate: string;
  visitsChange: string;
  uniqueVisitorsChange: string;
  pageViewsChange: string;
  avgSessionDurationChange: string;
  bounceRateChange: string;
}

export interface QuickMetric {
  key: string;
  labelVi: string;
  labelKr: string;
  value: string;
  subTextVi: string;
  subTextKr: string;
  icon: string;
  color: string;
}

export interface AnalyticsData {
  traffic: DailyTraffic[];
  topPages: TopPage[];
  topProducts: TopProduct[];
  referrerSources: ReferrerSource[];
  hourlyTraffic: HourlyTraffic[];
  geoData: GeoData[];
  metrics: QuickMetric[];
  totalVisits: number;
  totalPageViews: number;
  uniqueVisitors: number;
  avgSessionDuration: string;
  bounceRate: string;
  activeUsers: number;
  deviceBreakdown: Record<string, number>;
  comparison: PeriodComparison;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fillMissingDates(
  startDate: string,
  endDate: string,
  rows: { date: string; total_visits: number | null; unique_visitors: number | null; total_page_views: number | null }[]
): DailyTraffic[] {
  const map = new Map<string, { visits: number; unique: number; pageViews: number }>();
  rows.forEach((r) => {
    map.set(r.date, {
      visits: r.total_visits ?? 0,
      unique: r.unique_visitors ?? 0,
      pageViews: r.total_page_views ?? 0,
    });
  });

  const result: DailyTraffic[] = [];
  const s = new Date(startDate);
  const e = new Date(endDate);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    const item = map.get(key);
    result.push({
      date: key,
      label: d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }),
      visits: item?.visits ?? 0,
      unique: item?.unique ?? 0,
      pageViews: item?.pageViews ?? 0,
    });
  }
  return result;
}

export function useAnalyticsData(startDate: string, endDate: string) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch daily traffic summary
      const { data: summaryRows, error: summaryError } = await supabase
        .from("analytics_daily_summary")
        .select("date, total_visits, unique_visitors, total_page_views")
        .gte("date", startDate)
        .lte("date", endDate)
        .order("date", { ascending: true });

      if (summaryError) throw summaryError;

      const traffic = fillMissingDates(startDate, endDate, summaryRows || []);
      const totalVisits = traffic.reduce((sum, t) => sum + t.visits, 0);
      const totalPageViews = traffic.reduce((sum, t) => sum + t.pageViews, 0);
      const uniqueVisitors = traffic.reduce((sum, t) => sum + t.unique, 0);

      // 2. Top pages (from page_views)
      const { data: topPagesRaw } = await supabase
        .from("page_views")
        .select("page_path")
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`);

      const pageCounts: Record<string, number> = {};
      (topPagesRaw || []).forEach((p) => {
        pageCounts[p.page_path] = (pageCounts[p.page_path] || 0) + 1;
      });
      const topPages: TopPage[] = Object.entries(pageCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([page_path, views]) => ({ page_path, views }));

      // 3. Top products
      const { data: topProductsRaw } = await supabase
        .from("product_views")
        .select("product_name, product_slug")
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`);

      const productCounts: Record<string, { name: string; slug: string; views: number }> = {};
      (topProductsRaw || []).forEach((p) => {
        const key = p.product_slug || p.product_name;
        if (productCounts[key]) {
          productCounts[key].views += 1;
        } else {
          productCounts[key] = { name: p.product_name, slug: p.product_slug, views: 1 };
        }
      });
      const topProducts: TopProduct[] = Object.values(productCounts)
        .sort((a, b) => b.views - a.views)
        .slice(0, 10)
        .map((p) => ({ product_name: p.name, product_slug: p.slug, views: p.views }));

      // 4. Sessions for metrics
      const { data: sessionsRaw } = await supabase
        .from("user_sessions")
        .select("start_time, end_time, page_count, device_type")
        .gte("start_time", `${startDate}T00:00:00`)
        .lte("start_time", `${endDate}T23:59:59`);

      let totalDuration = 0;
      let bounceCount = 0;
      const deviceBreakdown: Record<string, number> = {};
      (sessionsRaw || []).forEach((s) => {
        if (s.end_time && s.start_time) {
          const dur = (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 1000;
          if (dur > 0 && dur < 36000) totalDuration += dur;
        }
        if ((s.page_count || 1) <= 1) bounceCount += 1;
        const dev = s.device_type || "unknown";
        deviceBreakdown[dev] = (deviceBreakdown[dev] || 0) + 1;
      });

      const sessionCount = sessionsRaw?.length || 0;
      const avgSessionDuration = sessionCount > 0 ? formatDuration(Math.round(totalDuration / sessionCount)) : "0:00";
      const bounceRate = sessionCount > 0 ? ((bounceCount / sessionCount) * 100).toFixed(1) : "0.0";

      // 5. Active users (sessions started in last 30 minutes)
      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { count: activeUsers } = await supabase
        .from("user_sessions")
        .select("*", { count: "exact", head: true })
        .gte("start_time", thirtyMinAgo);

      // 6. Referrer sources
      const { data: referrerRaw } = await supabase
        .from("user_sessions")
        .select("referrer, landing_page")
        .gte("start_time", `${startDate}T00:00:00`)
        .lte("start_time", `${endDate}T23:59:59`);

      const referrerCounts: Record<string, number> = {};
      (referrerRaw || []).forEach((s) => {
        let ref = s.referrer || "";
        if (!ref || ref === "") {
          ref = "Trực tiếp / Không rõ";
        } else {
          try {
            const url = new URL(ref);
            ref = url.hostname.replace(/^www\./, "");
          } catch {
            ref = ref.length > 40 ? ref.slice(0, 40) + "..." : ref;
          }
        }
        referrerCounts[ref] = (referrerCounts[ref] || 0) + 1;
      });
      const referrerSources: ReferrerSource[] = Object.entries(referrerCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([source, count]) => ({ source, count }));

      // 7. Hourly traffic distribution
      const { data: hourlyRaw } = await supabase
        .from("page_views")
        .select("created_at")
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`);

      const hourlyCounts = Array(24).fill(0);
      (hourlyRaw || []).forEach((p) => {
        const h = new Date(p.created_at).getHours();
        if (h >= 0 && h < 24) hourlyCounts[h] += 1;
      });
      const hourlyTraffic: HourlyTraffic[] = hourlyCounts.map((count, hour) => ({
        hour: `${hour.toString().padStart(2, "0")}:00`,
        count,
      }));

      // 8. Geographic data
      const { data: geoRaw } = await supabase
        .from("user_sessions")
        .select("country, city")
        .gte("start_time", `${startDate}T00:00:00`)
        .lte("start_time", `${endDate}T23:59:59`);

      const geoCounts: Record<string, GeoData> = {};
      (geoRaw || []).forEach((s) => {
        const country = s.country || "Không rõ";
        const city = s.city || "Không rõ";
        const key = `${country}|${city}`;
        if (geoCounts[key]) {
          geoCounts[key].count += 1;
        } else {
          geoCounts[key] = { country, city, count: 1 };
        }
      });
      const geoData: GeoData[] = Object.values(geoCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 9. Calculate trends compared to previous period
      const daysDiff = Math.max(1, Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000));
      const prevStart = new Date(new Date(startDate).getTime() - daysDiff * 86400000).toISOString().slice(0, 10);
      const prevEnd = new Date(new Date(startDate).getTime() - 86400000).toISOString().slice(0, 10);

      const { data: prevSummary } = await supabase
        .from("analytics_daily_summary")
        .select("total_visits, unique_visitors, total_page_views")
        .gte("date", prevStart)
        .lte("date", prevEnd);
      const prevVisits = (prevSummary || []).reduce((sum, r) => sum + (r.total_visits ?? 0), 0);
      const prevUniqueVisitors = (prevSummary || []).reduce((sum, r) => sum + (r.unique_visitors ?? 0), 0);
      const prevPageViews = (prevSummary || []).reduce((sum, r) => sum + (r.total_page_views ?? 0), 0);

      const { data: prevSessions } = await supabase
        .from("user_sessions")
        .select("start_time, end_time, page_count")
        .gte("start_time", `${prevStart}T00:00:00`)
        .lte("start_time", `${prevEnd}T23:59:59`);

      let prevTotalDuration = 0;
      let prevBounceCount = 0;
      (prevSessions || []).forEach((s) => {
        if (s.end_time && s.start_time) {
          const dur = (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 1000;
          if (dur > 0 && dur < 36000) prevTotalDuration += dur;
        }
        if ((s.page_count || 1) <= 1) prevBounceCount += 1;
      });
      const prevSessionCount = prevSessions?.length || 0;
      const prevAvgDuration = prevSessionCount > 0 ? formatDuration(Math.round(prevTotalDuration / prevSessionCount)) : "0:00";
      const prevBounceRate = prevSessionCount > 0 ? ((prevBounceCount / prevSessionCount) * 100).toFixed(1) : "0.0";

      const calcChange = (curr: number, prev: number) => {
        if (prev <= 0) return curr > 0 ? "+100.0%" : "0.0%";
        return `${((curr - prev) / prev * 100) >= 0 ? "+" : ""}${((curr - prev) / prev * 100).toFixed(1)}%`;
      };
      const calcChangeStr = (currStr: string, prevStr: string) => {
        const curr = parseFloat(currStr.replace("%", ""));
        const prev = parseFloat(prevStr.replace("%", ""));
        if (prev <= 0) return curr > 0 ? "+100.0%" : "0.0%";
        return `${((curr - prev) / prev * 100) >= 0 ? "+" : ""}${((curr - prev) / prev * 100).toFixed(1)}%`;
      };

      const currentCount = totalVisits;
      const trendVisits = prevVisits > 0 ? (((currentCount - prevVisits) / prevVisits) * 100).toFixed(1) : "0.0";
      const trendVisitsStr = `${parseFloat(trendVisits) >= 0 ? "+" : ""}${trendVisits}%`;

      const comparison: PeriodComparison = {
        visits: prevVisits,
        uniqueVisitors: prevUniqueVisitors,
        pageViews: prevPageViews,
        avgSessionDuration: prevAvgDuration,
        bounceRate: `${prevBounceRate}%`,
        visitsChange: calcChange(totalVisits, prevVisits),
        uniqueVisitorsChange: calcChange(uniqueVisitors, prevUniqueVisitors),
        pageViewsChange: calcChange(totalPageViews, prevPageViews),
        avgSessionDurationChange: calcChangeStr(avgSessionDuration, prevAvgDuration),
        bounceRateChange: calcChangeStr(`${bounceRate}%`, `${prevBounceRate}%`),
      };

      const metrics: QuickMetric[] = [
        {
          key: "visits",
          labelVi: "Tổng lượt truy cập",
          labelKr: "총 방문수",
          value: totalVisits.toLocaleString(),
          subTextVi: `${trendVisitsStr} so với kỳ trước`,
          subTextKr: `이전 기간 대비 ${trendVisitsStr}`,
          icon: "ri-global-line",
          color: "#004A99",
        },
        {
          key: "bounce",
          labelVi: "Tỷ lệ thoát",
          labelKr: "이탈률",
          value: `${bounceRate}%`,
          subTextVi: `${parseFloat(bounceRate) < 40 ? "Tốt" : "Cần cải thiện"}`,
          subTextKr: `${parseFloat(bounceRate) < 40 ? "양호" : "개선 필요"}`,
          icon: "ri-arrow-go-back-line",
          color: "#EF4444",
        },
        {
          key: "time",
          labelVi: "Thời gian TB",
          labelKr: "평균 체류 시간",
          value: avgSessionDuration,
          subTextVi: "Trên mỗi phiên",
          subTextKr: "세션당",
          icon: "ri-time-line",
          color: "#10B981",
        },
        {
          key: "active",
          labelVi: "Người dùng đang hoạt động",
          labelKr: "활성 사용자",
          value: (activeUsers ?? 0).toLocaleString(),
          subTextVi: "30 phút qua",
          subTextKr: "최근 30분",
          icon: "ri-user-voice-line",
          color: "#F59E0B",
        },
      ];

      setData({
        traffic,
        topPages,
        topProducts,
        referrerSources,
        hourlyTraffic,
        geoData,
        metrics,
        totalVisits,
        totalPageViews,
        uniqueVisitors,
        avgSessionDuration,
        bounceRate,
        activeUsers: activeUsers ?? 0,
        deviceBreakdown,
        comparison,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Realtime subscription on analytics_daily_summary for instant updates
  useEffect(() => {
    const channel = supabase
      .channel("analytics-daily-summary")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "analytics_daily_summary" },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          if (!row || !row.date) return;
          const dateStr = String(row.date);
          const s = new Date(startDate);
          const e = new Date(endDate);
          const d = new Date(dateStr);
          if (d < s || d > e) return;

          setData((prev) => {
            if (!prev) return prev;
            const idx = prev.traffic.findIndex((t) => t.date === dateStr);
            let newTraffic: DailyTraffic[];
            if (idx >= 0) {
              newTraffic = prev.traffic.map((t, i) =>
                i === idx
                  ? {
                      ...t,
                      visits: (row.total_visits as number) ?? t.visits,
                      unique: (row.unique_visitors as number) ?? t.unique,
                      pageViews: (row.total_page_views as number) ?? t.pageViews,
                    }
                  : t
              );
            } else {
              const newItem: DailyTraffic = {
                date: dateStr,
                label: d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }),
                visits: (row.total_visits as number) || 0,
                unique: (row.unique_visitors as number) || 0,
                pageViews: (row.total_page_views as number) || 0,
              };
              newTraffic = [...prev.traffic, newItem].sort((a, b) =>
                a.date.localeCompare(b.date)
              );
            }
            const newTotalVisits = newTraffic.reduce((sum, t) => sum + t.visits, 0);
            const newTotalPageViews = newTraffic.reduce((sum, t) => sum + t.pageViews, 0);
            const newUniqueVisitors = newTraffic.reduce((sum, t) => sum + t.unique, 0);
            const newMetrics = prev.metrics.map((m) => {
              if (m.key === "visits") {
                return { ...m, value: newTotalVisits.toLocaleString() };
              }
              return m;
            });
            return {
              ...prev,
              traffic: newTraffic,
              metrics: newMetrics,
              totalVisits: newTotalVisits,
              totalPageViews: newTotalPageViews,
              uniqueVisitors: newUniqueVisitors,
            };
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "analytics_daily_summary" },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          if (!row || !row.date) return;
          const dateStr = String(row.date);
          const s = new Date(startDate);
          const e = new Date(endDate);
          const d = new Date(dateStr);
          if (d < s || d > e) return;

          setData((prev) => {
            if (!prev) return prev;
            const idx = prev.traffic.findIndex((t) => t.date === dateStr);
            let newTraffic: DailyTraffic[];
            if (idx >= 0) {
              newTraffic = prev.traffic.map((t, i) =>
                i === idx
                  ? {
                      ...t,
                      visits: (row.total_visits as number) ?? t.visits,
                      unique: (row.unique_visitors as number) ?? t.unique,
                      pageViews: (row.total_page_views as number) ?? t.pageViews,
                    }
                  : t
              );
            } else {
              const newItem: DailyTraffic = {
                date: dateStr,
                label: d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }),
                visits: (row.total_visits as number) || 0,
                unique: (row.unique_visitors as number) || 0,
                pageViews: (row.total_page_views as number) || 0,
              };
              newTraffic = [...prev.traffic, newItem].sort((a, b) =>
                a.date.localeCompare(b.date)
              );
            }
            const newTotalVisits = newTraffic.reduce((sum, t) => sum + t.visits, 0);
            const newTotalPageViews = newTraffic.reduce((sum, t) => sum + t.pageViews, 0);
            const newUniqueVisitors = newTraffic.reduce((sum, t) => sum + t.unique, 0);
            const newMetrics = prev.metrics.map((m) => {
              if (m.key === "visits") {
                return { ...m, value: newTotalVisits.toLocaleString() };
              }
              return m;
            });
            return {
              ...prev,
              traffic: newTraffic,
              metrics: newMetrics,
              totalVisits: newTotalVisits,
              totalPageViews: newTotalPageViews,
              uniqueVisitors: newUniqueVisitors,
            };
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [startDate, endDate]);

  return { data, loading, error, refetch: fetchData };
}